import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Loader2, PlayCircle, Brain, Target, AlertTriangle,
  TrendingUp, FileText, Info,
} from "lucide-react";

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch { return iso; }
}

function ChipTone({ level, children, testId }) {
  const cls = ["critical", "high", "medium", "low"].includes(level)
    ? `hw-exec__ai-chip hw-exec__ai-chip--${level}`
    : "hw-exec__ai-chip";
  return <span className={cls} data-testid={testId}>{children}</span>;
}

function AtRiskList({ items }) {
  if (!items || items.length === 0) {
    return <div style={{ color: "#C4B5FD", fontSize: "0.8rem" }}>
      No at-risk controls predicted — clean posture.
    </div>;
  }
  return (
    <ul className="hw-exec__ai-list" data-testid="ai-at-risk-list">
      {items.map((r, i) => (
        <li key={r.control_id ? `${r.control_id}-${i}` : i}
            className="hw-exec__ai-item"
            data-testid={`ai-at-risk-${r.control_id || i}`}>
          <div><strong>{r.control_id || "—"}</strong> · {r.title || "Untitled"}</div>
          <div style={{ marginTop: "0.3rem", color: "#E9D5FF" }}>{r.risk_reason || r.reason}</div>
          <div className="hw-exec__ai-item-meta">
            {r.framework && (
              <ChipTone>{String(r.framework).toUpperCase()}</ChipTone>
            )}
            {r.probability && (
              <ChipTone level={r.probability === "high" ? "high" :
                              r.probability === "medium" ? "medium" : "low"}>
                {r.probability} likelihood
              </ChipTone>
            )}
            {r.suggested_action && (
              <span style={{ color: "#DDD6FE" }}>→ {r.suggested_action}</span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function EvidenceList({ items }) {
  if (!items || items.length === 0) {
    return <div style={{ color: "#C4B5FD", fontSize: "0.8rem" }}>
      No evidence gaps identified.
    </div>;
  }
  return (
    <ul className="hw-exec__ai-list" data-testid="ai-evidence-list">
      {items.map((r, i) => (
        <li key={i} className="hw-exec__ai-item"
            data-testid={`ai-evidence-${r.control_id || i}`}>
          <div>
            <strong>{r.control_id || "—"}</strong> needs {r.evidence_type_needed || "evidence"}
          </div>
          <div style={{ marginTop: "0.3rem", color: "#E9D5FF" }}>{r.rationale}</div>
          <div className="hw-exec__ai-item-meta">
            {r.framework && <ChipTone>{String(r.framework).toUpperCase()}</ChipTone>}
            {r.priority && (
              <ChipTone level={r.priority}>{r.priority} priority</ChipTone>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function ExecRecList({ items }) {
  if (!items || items.length === 0) {
    return <div style={{ color: "#C4B5FD", fontSize: "0.8rem" }}>
      No executive recommendations at this time.
    </div>;
  }
  return (
    <ul className="hw-exec__ai-list" data-testid="ai-exec-rec-list">
      {items.map((r, i) => (
        <li key={i} className="hw-exec__ai-item"
            data-testid={`ai-exec-rec-${i}`}>
          <div><strong>{r.title || "Recommendation"}</strong></div>
          <div style={{ marginTop: "0.3rem", color: "#E9D5FF" }}>{r.description}</div>
          <div className="hw-exec__ai-item-meta">
            {r.impact && (
              <ChipTone level={r.impact === "high" ? "high" :
                              r.impact === "medium" ? "medium" : "low"}>
                {r.impact} impact
              </ChipTone>
            )}
            {r.effort && <ChipTone>{r.effort} effort</ChipTone>}
            {r.timeline_days != null && <ChipTone>{r.timeline_days}d</ChipTone>}
            {r.owner_role && <ChipTone>{r.owner_role}</ChipTone>}
          </div>
        </li>
      ))}
    </ul>
  );
}

function ForecastPanel({ forecast }) {
  if (!forecast) return null;
  const cols = [
    { key: "next_14_days", label: "Next 14 days" },
    { key: "next_30_days", label: "Next 30 days" },
    { key: "next_90_days", label: "Next 90 days" },
  ];
  return (
    <div data-testid="ai-forecast">
      <div className="hw-exec__ai-forecast-grid">
        {cols.map((c) => {
          const b = forecast[c.key] || {};
          return (
            <div className="hw-exec__ai-forecast-col" key={c.key}
                 data-testid={`ai-forecast-${c.key}`}>
              <div className="hw-exec__ai-forecast-col-label">{c.label}</div>
              <div className="hw-exec__ai-forecast-col-rows">
                <div><ChipTone level="critical">C</ChipTone> {b.critical ?? 0}</div>
                <div><ChipTone level="high">H</ChipTone> {b.high ?? 0}</div>
                <div><ChipTone level="medium">M</ChipTone> {b.medium ?? 0}</div>
                <div><ChipTone level="low">L</ChipTone> {b.low ?? 0}</div>
              </div>
            </div>
          );
        })}
      </div>
      {forecast.narrative && (
        <div className="hw-exec__ai-forecast-narr" data-testid="ai-forecast-narrative">
          &ldquo;{forecast.narrative}&rdquo;
        </div>
      )}
    </div>
  );
}

export default function AiPredictivePanel() {
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ai-compliance/latest");
      setLatest(data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load AI predictions"));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    if (running) return;
    setRunning(true);

    // Capture the current computed_at so we can detect when a fresh prediction lands.
    const prevAt = latest?.predictions?.computed_at || null;

    // Fire the POST. We don't await it directly — Cloudflare / K8s ingress in
    // front of the preview URL times out at ~60s, but Claude Sonnet 4.6 can
    // take 45-120s. Instead we start a parallel poller against /latest that
    // detects a new `computed_at` and swaps in the fresh payload.
    let finished = false;
    let hitError = null;

    const runPromise = api.post("/admin/ai-compliance/run")
      .then((resp) => {
        // The synchronous path completed inside the timeout — happy path.
        if (finished) return;
        finished = true;
        if (resp?.data) {
          setLatest({ has_prediction: true, predictions: resp.data });
          if (resp.data.ok) toast.success("AI predictions refreshed");
          else toast.error(`AI run completed with errors: ${resp.data.error || "unknown"}`);
        }
      })
      .catch((e) => {
        // 502 / 504 / network abort — expected on long LLM runs behind CDN.
        // The backend keeps working; the poller will pick up the result.
        hitError = e;
      });

    // Poll /latest every 4s for up to 180s (LLM ceiling).
    const started = Date.now();
    const POLL_INTERVAL_MS = 4000;
    const POLL_TIMEOUT_MS = 180000;
    while (!finished && Date.now() - started < POLL_TIMEOUT_MS) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (finished) break;
      try {
        const { data } = await api.get("/admin/ai-compliance/latest");
        const newAt = data?.predictions?.computed_at;
        if (data?.has_prediction && newAt && newAt !== prevAt) {
          finished = true;
          setLatest(data);
          if (data.predictions.ok) toast.success("AI predictions refreshed");
          else toast.error(`AI run completed with errors: ${data.predictions.error || "unknown"}`);
          break;
        }
      } catch (_ignored) {
        // Poll failures are non-fatal — try again next tick.
      }
    }

    // Wait for the original POST to settle so we don't leak the promise.
    await runPromise;

    if (!finished) {
      // We waited 180s and still didn't see a fresh prediction. Surface a
      // friendly message rather than the raw CDN error body.
      if (hitError) {
        toast.error("The AI analysis is still running. We'll refresh automatically once it completes — try clicking Analyze again in a moment.");
      } else {
        toast.error("AI analysis timed out. Please try again in a minute.");
      }
    }

    setRunning(false);
  };

  const doc = latest?.predictions;
  const preds = doc?.predictions;
  const hasPred = latest?.has_prediction && preds;

  return (
    <section className="hw-exec__ai" data-testid="exec-ai-slot">
      <div className="hw-exec__ai-head">
        <div>
          <h3>
            <Sparkles className="w-5 h-5" /> AI Predictive Compliance
          </h3>
          {doc && (
            <div className="hw-exec__ai-meta" style={{ marginTop: "0.35rem" }}>
              <span>Generated {fmtWhen(doc.computed_at)}</span>
              {doc.model && (
                <span>Model: {doc.model.provider}/{doc.model.name}</span>
              )}
              {doc.triggered_by && <span>Triggered: {doc.triggered_by}</span>}
              {doc.duration_ms != null && <span>{Math.round(doc.duration_ms / 1000)}s</span>}
            </div>
          )}
        </div>
        <button type="button" className="hw-exec__ai-btn"
                onClick={runNow} disabled={running || loading}
                data-testid="ai-run-now">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
          {running ? "Analyzing…" : hasPred ? "Re-analyze now" : "Analyze now"}
        </button>
      </div>

      {loading ? (
        <div className="hw-exec__ai-empty">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading predictions…
        </div>
      ) : !hasPred ? (
        <div className="hw-exec__ai-empty">
          <Brain className="w-6 h-6" />
          <div>No AI predictions yet.</div>
          <div style={{ fontSize: "0.78rem", color: "#A78BFA" }}>
            Click <strong>Analyze now</strong> to generate an executive-ready
            compliance forecast powered by Claude Sonnet 4.6. The nightly job
            will keep this fresh every day at 05:15 UTC.
          </div>
        </div>
      ) : (
        <>
          {doc.error && (
            <div className="hw-exec__ai-error" data-testid="ai-error">
              <Info className="w-4 h-4" style={{ display: "inline", marginRight: "0.3rem", verticalAlign: "text-bottom" }} />
              Latest run reported an issue: {doc.error}. Displaying the last
              successful state below.
            </div>
          )}
          <div className="hw-exec__ai-sections">
            <div className="hw-exec__ai-section" data-testid="ai-section-at-risk">
              <h4><AlertTriangle className="w-3.5 h-3.5" /> Predicted At-Risk Controls</h4>
              <AtRiskList items={preds.at_risk_controls} />
            </div>
            <div className="hw-exec__ai-section" data-testid="ai-section-evidence">
              <h4><FileText className="w-3.5 h-3.5" /> Evidence Mapping Recommendations</h4>
              <EvidenceList items={preds.evidence_recommendations} />
            </div>
            <div className="hw-exec__ai-section" data-testid="ai-section-exec-rec">
              <h4><Target className="w-3.5 h-3.5" /> Executive Recommendations</h4>
              <ExecRecList items={preds.executive_recommendations} />
            </div>
            <div className="hw-exec__ai-section" data-testid="ai-section-forecast">
              <h4><TrendingUp className="w-3.5 h-3.5" /> Risk Forecast</h4>
              <ForecastPanel forecast={preds.risk_forecast} />
            </div>
          </div>
        </>
      )}
    </section>
  );
}
